import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { hasPermission } from '../utils/navPermissions';
import { canViewFeature, canAssignClassTeachers } from '../utils/featurePermissions';
import { getSchoolFeatures, isSchoolFeatureEnabled } from '../utils/schoolFeatures';
import { resolveNotificationLink } from '../utils/notificationLinks';
import { dispatchNotificationsRead, isNotificationUnread, markNotificationRead } from '../utils/notifications';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import BranchPickerMenu from './settings/BranchPickerMenu';
import { Link } from 'react-router-dom';
import { isMobileContainer } from '../utils/device';
import {
  Users, GraduationCap, School, IndianRupee, CheckCircle, XCircle,
  Clock, TrendingUp, Calendar, Building2, Mail, UserCircle
} from 'lucide-react';
import { formatTimeRange12h } from '../utils/timeFormat';
import {
  attendanceStatusLabel,
  attendanceStatusClass,
  SchoolHoursCard,
  TodayAttendanceCard,
} from './portal/PortalScheduleSummary';
import useBranchLabels from '../hooks/useBranchLabels';
import BranchLabel from './ui/BranchLabel';
import UpgradePromoBanner from './subscription/UpgradePromoBanner';

const Dashboard = () => {
  const { user } = useAuth();
  const { showBranchLabels, formatLabel } = useBranchLabels();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classTeacherRows, setClassTeacherRows] = useState([]);
  const [classesSummary, setClassesSummary] = useState([]);

  const canSeeClassTeachers = canAssignClassTeachers(user);
  const canSeeClassesSummary = hasPermission(user, 'view_school') || canViewFeature(user, 'classes');
  const canSeeFees = canViewFeature(user, 'fees');
  const canSeeAttendanceOverview = canViewFeature(user, 'attendance');

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  const fetchClassTeachers = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const res = await api.get('/classes', { params });
      const classes = res.data.data || [];
      setClassesSummary(
        classes.map((c) => ({
          id: c.id,
          name: c.name,
          branch_name: c.branch_name,
          sectionCount: (c.sections || []).length,
        })),
      );
      const rows = [];
      classes.forEach((c) => {
        const secondary = `${c.secondary_class_teacher_first_name || ''} ${c.secondary_class_teacher_last_name || ''}`.trim();
        const primaryClass = `${c.class_level_teacher_first_name || ''} ${c.class_level_teacher_last_name || ''}`.trim();
        if (primaryClass || secondary) {
          rows.push({
            key: `c-${c.id}`,
            className: c.name,
            sectionName: '—',
            primaryName: primaryClass || '—',
            secondaryName: secondary || null,
          });
          return;
        }
        (c.sections || []).forEach((s) => {
          const name = `${s.class_teacher_first_name || ''} ${s.class_teacher_last_name || ''}`.trim();
          if (!name) return;
          rows.push({
            key: `${c.id}-${s.id}`,
            className: c.name,
            sectionName: s.name,
            primaryName: name,
            secondaryName: secondary || null,
          });
        });
      });
      setClassTeacherRows(rows);
    } catch (e) {
      setClassTeacherRows([]);
      setClassesSummary([]);
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const onBranchSwitched = () => {
      setLoading(true);
      fetchDashboard().catch(() => {});
      if (canSeeClassTeachers || canSeeClassesSummary) {
        fetchClassTeachers().catch(() => {});
      }
    };
    window.addEventListener('schoolerp:branch-switched', onBranchSwitched);
    return () => window.removeEventListener('schoolerp:branch-switched', onBranchSwitched);
  }, [fetchDashboard, canSeeClassTeachers, canSeeClassesSummary, user?.school_id]);

  useEffect(() => {
    if (canSeeClassTeachers || canSeeClassesSummary) {
      fetchClassTeachers().catch(() => {});
    }
  }, [canSeeClassTeachers, canSeeClassesSummary, user?.school_id]);

  useEffect(() => {
    const handler = () => {
      if (canSeeClassTeachers || canSeeClassesSummary) {
        fetchClassTeachers().catch(() => {});
      }
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [canSeeClassTeachers, canSeeClassesSummary, user?.school_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const kind = dashboardData?.dashboard_kind || user?.role_slug;
  const stats = dashboardData?.stats || {};
  const attendance = dashboardData?.attendance || {};

  const welcomeHeader = (title, subtitle, gradient = 'from-blue-600 to-blue-800') => (
    <div className={`bg-gradient-to-r ${gradient} rounded-2xl p-6 text-white`}>
      <h1 className="text-2xl font-bold mb-2">{title}</h1>
      <p className="text-blue-100 opacity-90">{subtitle}</p>
    </div>
  );

  const splitHonorificFromName = (rawName) => {
    const name = (rawName || '').toString().trim();
    if (!name) return { honorific: null, name: '' };
    const parts = name.split(/\s+/).filter(Boolean);
    const first = (parts[0] || '').replace(/\./g, '').toLowerCase();
    if (first === 'mr' || first === 'mrs' || first === 'ms') {
      const honorific = first === 'mr' ? 'Mr.' : first === 'mrs' ? 'Mrs.' : 'Ms.';
      return { honorific, name: parts.slice(1).join(' ') };
    }
    return { honorific: null, name };
  };

  const schoolFeatures = getSchoolFeatures(user);
  const userId = parseInt(user?.id, 10);
  const isExtraLeaveApprover = userId > 0
    && (schoolFeatures.leave_extra_approver_user_ids || []).includes(userId);
  const canSeeLeavePending = canViewFeature(user, 'leave')
    || hasPermission(user, 'approve_leave')
    || hasPermission(user, 'manage_leave')
    || isExtraLeaveApprover;

  if (kind === 'teacher') {
    const schedule = dashboardData?.today_schedule || [];
    const classes = dashboardData?.assigned_classes || [];
    const pendingLeave = dashboardData?.pending_leave_count ?? 0;
    return (
      <div className="space-y-6" data-testid="dashboard">
        {welcomeHeader(
          `Hello, ${user?.first_name || 'Teacher'}`,
          'Your classes and today’s timetable.',
          'from-emerald-600 to-teal-800',
        )}
        <div className={`grid grid-cols-1 gap-4 ${canSeeLeavePending ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <StatCard title="Assigned classes" value={classes.length} icon={School} color="green" />
          <StatCard title="Periods today" value={schedule.length} icon={Clock} color="blue" />
          {canSeeLeavePending ? (
            <Link to="/leave" className="block">
              <StatCard
                title="Pending leave"
                value={pendingLeave}
                icon={Calendar}
                color={pendingLeave > 0 ? 'amber' : 'blue'}
              />
              {pendingLeave === 0 ? (
                <p className="text-xs text-muted-foreground mt-1 px-1">No applications awaiting you</p>
              ) : (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 px-1 font-medium">
                  Tap to review leave applications
                </p>
              )}
            </Link>
          ) : null}
        </div>
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Today&apos;s schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {schedule.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No periods scheduled today</p>
            ) : (
              schedule.map((row) => (
                <div key={row.id} className="flex justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                  <div>
                    <p className="font-medium text-foreground">{row.subject_name}</p>
                    <p className="text-sm text-muted-foreground">{row.class_name}</p>
                  </div>
                  <p className="text-sm text-muted-foreground shrink-0">
                    {formatTimeRange12h(row.start_time, row.end_time)}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <AnnouncementsCard items={dashboardData?.announcements} />
      </div>
    );
  }

  if (kind === 'student') {
    const student = dashboardData?.student;
    const attRows = dashboardData?.attendance_summary || [];
    const present = attRows.find((a) => a.status === 'present')?.count || 0;
    const absent = attRows.find((a) => a.status === 'absent')?.count || 0;
    const absentItems = dashboardData?.absent_notifications || [];
    const generalNotifications = (dashboardData?.recent_notifications || []).filter(
      (n) => !(n.title || '').startsWith('Absent:'),
    );
    const notificationItems = [...absentItems, ...generalNotifications].slice(0, 5);
    return (
      <div className="space-y-6" data-testid="dashboard">
        {welcomeHeader(
          `Hello, ${user?.first_name || 'Student'}`,
          student ? `${student.class_name || '—'}${student.section_name ? ` · ${student.section_name}` : ''}` : 'Your school portal',
          'from-indigo-600 to-violet-800',
        )}
        {student && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Present (all time)" value={present} icon={CheckCircle} color="green" to="/portal/student/attendance" />
            <StatCard title="Absent (all time)" value={absent} icon={XCircle} color="amber" to="/portal/student/attendance" />
            <StatCard
              title="Fees pending"
              value={`₹${(dashboardData?.fee_pending || 0).toLocaleString('en-IN')}`}
              icon={IndianRupee}
              color="purple"
              to="/portal/student/fees"
            />
          </div>
        )}
        {student && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SchoolHoursCard hours={dashboardData?.school_hours} />
            <TodayAttendanceCard todayAttendance={dashboardData?.today_attendance} />
          </div>
        )}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Recent results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(dashboardData?.recent_results || []).length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No results yet</p>
            ) : (
              dashboardData.recent_results.map((r) => (
                <div key={r.id} className="p-3 bg-muted/50 rounded-lg border border-border flex justify-between">
                  <span className="text-foreground">{r.subject_name} · {r.exam_name}</span>
                  <span className="font-medium text-foreground">{r.marks_obtained ?? '—'}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <div className="flex flex-wrap gap-2">
          <Link to="/portal/student/academics" className="text-sm text-primary hover:underline">My class</Link>
          <Link to="/portal/student/timetable" className="text-sm text-primary hover:underline">Timetable</Link>
          <Link to="/portal/student/homework" className="text-sm text-primary hover:underline">Homework</Link>
          <Link to="/portal/student/attendance" className="text-sm text-primary hover:underline">Attendance</Link>
          <Link to="/portal/student/fees" className="text-sm text-primary hover:underline">Fees</Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <NotificationsCard items={notificationItems} homeworkPath="/portal/student/homework" />
          <AssignmentsCard items={dashboardData?.recent_assignments} homeworkPath="/portal/student/homework" />
        </div>
        <AnnouncementsCard items={dashboardData?.announcements} />
      </div>
    );
  }

  if (kind === 'parent') {
    const children = dashboardData?.children || [];
    const absentItems = dashboardData?.absent_notifications || [];
    const generalNotifications = (dashboardData?.recent_notifications || []).filter(
      (n) => !(n.title || '').startsWith('Absent:'),
    );
    const notificationItems = [...absentItems, ...generalNotifications].slice(0, 5);
    const rawParentName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Parent';
    const parsed = splitHonorificFromName(rawParentName);
    const honorific = parsed.honorific || 'Mr./Mrs./Ms.';
    const parentName = parsed.name || rawParentName;
    return (
      <div className="space-y-6" data-testid="dashboard">
        {welcomeHeader(
          `Hello, ${honorific} ${parentName}`,
          'Overview for your children.',
          'from-rose-600 to-pink-800',
        )}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground">My children</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {children.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No linked students found</p>
            ) : (
              children.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">
                      {c.first_name} {c.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {c.class_name || '—'}
                      {c.admission_no ? ` · ${c.admission_no}` : ''}
                    </p>
                    {c.school_hours?.start_time && c.school_hours?.end_time && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Hours: {formatTimeRange12h(c.school_hours.start_time, c.school_hours.end_time)}
                      </p>
                    )}
                    <p className="text-xs mt-1">
                      <span className="text-muted-foreground">Today: </span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full ${attendanceStatusClass(c.today_attendance?.status)}`}>
                        {attendanceStatusLabel(c.today_attendance?.status)}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Pending fees</p>
                    <p className="font-semibold text-foreground">
                      ₹{(c.pending_fees ?? 0).toLocaleString()}
                    </p>
                    <Link to={`/portal/parent/child/${c.id}`} className="text-xs text-blue-400 hover:underline">
                      View profile
                    </Link>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <div className="flex flex-wrap gap-2">
          <Link to="/portal/parent/children" className="text-sm text-primary hover:underline">Children</Link>
          <Link to="/portal/parent/attendance" className="text-sm text-primary hover:underline">Attendance</Link>
          <Link to="/portal/parent/messages" className="text-sm text-primary hover:underline">Messages</Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <NotificationsCard items={notificationItems} homeworkPath="/portal/parent/homework" />
          <AssignmentsCard items={dashboardData?.recent_assignments} homeworkPath="/portal/parent/homework" showStudentName />
        </div>
        <AnnouncementsCard items={dashboardData?.announcements} />
      </div>
    );
  }

  if (kind === 'staff') {
    const pendingLeave = dashboardData?.pending_leave_count ?? 0;
    return (
      <div className="space-y-6" data-testid="dashboard">
        {welcomeHeader(
          `Hello, ${user?.first_name || 'Staff'}`,
          'School operations at a glance.',
          'from-slate-600 to-slate-800',
        )}
        <div className={`grid grid-cols-1 gap-4 ${canSeeLeavePending ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
          <StatCard title="Students" value={stats.total_students || 0} icon={GraduationCap} color="blue" />
          <StatCard title="Teachers" value={stats.total_teachers || 0} icon={Users} color="green" />
          <StatCard title="Classes" value={stats.total_classes || 0} icon={School} color="purple" />
          {canSeeLeavePending ? (
            <Link to="/leave" className="block">
              <StatCard
                title="Pending leave"
                value={pendingLeave}
                icon={Calendar}
                color={pendingLeave > 0 ? 'amber' : 'blue'}
              />
            </Link>
          ) : null}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AttendanceTodayCard attendance={attendance} />
          <AnnouncementsCard items={dashboardData?.announcements} />
        </div>
        <EventsCard events={dashboardData?.upcoming_events} />
      </div>
    );
  }

  if (kind === 'software_module') {
    const recentSchools = dashboardData?.recent_schools || [];
    const recentEnquiries = dashboardData?.recent_enquiries || [];

    return (
      <div className="space-y-6" data-testid="dashboard">
        <div className="bg-gradient-to-r from-violet-700 to-indigo-900 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">
            Welcome back, {user?.first_name} {user?.last_name}
          </h1>
          <p className="text-violet-100">Platform overview — schools onboarded and workforce across all tenants.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard title="Schools onboarded" value={stats.total_schools || 0} icon={Building2} color="blue" />
          <StatCard title="Active schools" value={stats.active_schools || 0} icon={School} color="green" />
          <StatCard title="Total users (accounts)" value={stats.total_users || 0} icon={UserCircle} color="purple" />
          <StatCard title="Teachers (all schools)" value={stats.total_teachers || 0} icon={Users} color="amber" />
          <StatCard title="Students (all schools)" value={stats.total_students || 0} icon={GraduationCap} color="blue" />
          <StatCard title="Lead enquiries" value={stats.total_enquiries || 0} icon={Mail} color="green" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Recent schools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentSchools.length > 0 ? (
                  recentSchools.map((s) => (
                    <div key={s.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border border-border">
                      <span className="text-foreground font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground capitalize">{s.status || '—'}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No schools yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-foreground">Recent enquiries</CardTitle>
              <Link to="/crm/inquiries" className="text-xs text-violet-400 hover:text-violet-300">
                View all
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentEnquiries.length > 0 ? (
                  recentEnquiries.map((en) => (
                    <div key={en.id} className="p-3 bg-muted/50 rounded-lg border border-border">
                      <p className="text-foreground font-medium">{en.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{en.email}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No enquiries yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (kind === 'school_module') {
    const recentSchools = dashboardData?.recent_schools || [];
    return (
      <div className="space-y-6" data-testid="dashboard">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">School onboarding</h1>
          <p className="text-blue-100">Manage schools from the Schools section.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard title="Total schools" value={stats.total_schools || 0} icon={School} color="blue" />
          <StatCard title="Active schools" value={stats.active_schools || 0} icon={Building2} color="green" />
        </div>
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Recently added</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentSchools.length > 0 ? (
                recentSchools.map((s) => (
                  <div key={s.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border border-border">
                    <span className="text-foreground font-medium">{s.name}</span>
                    <span className="text-xs text-muted-foreground capitalize">{s.status || '—'}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No schools yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const leadershipTitle = kind === 'principal' ? 'Principal dashboard' : 'School admin dashboard';
  const leadershipSubtitle = kind === 'principal'
    ? 'School-wide overview — academics, fees, and attendance.'
    : "Here's what's happening in your school today.";

  return (
    <div className="space-y-6" data-testid="dashboard">
      {user?.role_slug === 'school_admin' && (
        <div className="-mx-4 lg:-mx-8 -mt-4 lg:-mt-8">
          <UpgradePromoBanner user={user} variant="slim" />
        </div>
      )}

      {welcomeHeader(
        `${leadershipTitle} — ${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        leadershipSubtitle,
        kind === 'principal' ? 'from-violet-600 to-purple-800' : 'from-blue-600 to-blue-800',
      )}

      {user?.role_slug === 'school_admin' && isMobileContainer() && isSchoolFeatureEnabled(user, 'branches') ? (
        <Card className="border-border">
          <CardContent className="p-4 space-y-2">
            <Label className="text-muted-foreground">Active branch</Label>
            <BranchPickerMenu variant="dashboard" align="start" />
          </CardContent>
        </Card>
      ) : null}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {canSeeClassesSummary ? (
          <>
            <StatCard
              title="Total Students"
              value={stats.total_students || 0}
              icon={GraduationCap}
              color="blue"
            />
            <StatCard
              title="Total Teachers"
              value={stats.total_teachers || 0}
              icon={Users}
              color="green"
            />
            <StatCard
              title="Total Classes"
              value={stats.total_classes ?? 0}
              icon={School}
              color="purple"
            />
          </>
        ) : null}
        {canSeeLeavePending ? (
          <Link to="/leave" className="block">
            <StatCard
              title="Pending leave"
              value={dashboardData?.pending_leave_count ?? 0}
              icon={Calendar}
              color={(dashboardData?.pending_leave_count ?? 0) > 0 ? 'amber' : 'blue'}
            />
          </Link>
        ) : null}
        {canSeeFees ? (
          <StatCard
            title="Fee Collection"
            value={`₹${(stats.fee_collection_this_month || 0).toLocaleString()}`}
            icon={IndianRupee}
            color="amber"
          />
        ) : null}
      </div>

      {/* Attendance & Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {canSeeAttendanceOverview ? (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Today's Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{attendance.present || 0}</p>
                <p className="text-sm text-muted-foreground">Present</p>
              </div>
              <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{attendance.absent || 0}</p>
                <p className="text-sm text-muted-foreground">Absent</p>
              </div>
              <div className="text-center p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{attendance.late || 0}</p>
                <p className="text-sm text-muted-foreground">Late</p>
              </div>
            </div>
          </CardContent>
        </Card>
        ) : null}

        {canSeeFees ? (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Fee Collection Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.fee_chart || []).slice(-4).map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-20">{item.month}</span>
                  <div className="flex-1 bg-muted rounded-full h-4">
                    <div
                      className="bg-blue-500 h-4 rounded-full transition-all"
                      style={{ width: `${Math.min((item.amount / 100000) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-foreground w-24 text-right">
                    ₹{item.amount?.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        ) : null}
      </div>

      {/* Recent Activity & Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {canSeeClassesSummary && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Classes</CardTitle>
            </CardHeader>
            <CardContent>
              {classesSummary.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No classes for this academic year</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {classesSummary.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                      <div className="min-w-0">
                        <p className="text-foreground font-medium truncate flex items-center gap-2 flex-wrap">
                          {c.name}
                          <BranchLabel name={c.branch_name} show={showBranchLabels} />
                        </p>
                        <p className="text-xs text-muted-foreground">{c.sectionCount} section(s)</p>
                      </div>
                      <Link to={`/classes/${c.id}`} className="text-xs text-blue-400 hover:text-blue-300 shrink-0">
                        Open
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {canSeeClassTeachers && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Class teachers</CardTitle>
            </CardHeader>
            <CardContent>
              {classTeacherRows.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No class teachers assigned yet. Class detail after timetable periods.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {classTeacherRows.slice(0, 12).map((r) => (
                    <div key={r.key} className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                      <div className="min-w-0">
                        <p className="text-foreground font-medium truncate">{r.primaryName}</p>
                        {r.secondaryName ? (
                          <p className="text-xs text-amber-400/90 truncate">Secondary: {r.secondaryName}</p>
                        ) : null}
                        <p className="text-xs text-muted-foreground truncate">
                          {r.className}
                          {r.sectionName && r.sectionName !== '—' ? ` — Section ${r.sectionName}` : ''}
                        </p>
                      </div>
                      <Link to="/classes" className="text-xs text-blue-400 hover:text-blue-300 shrink-0">
                        Manage
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Announcements */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Recent Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.announcements || []).length > 0 ? (
                dashboardData.announcements.map((ann) => (
                  <div key={ann.id} className="p-3 bg-muted/50 rounded-lg border border-border">
                    <h4 className="font-medium text-foreground">{ann.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No announcements</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.upcoming_events || []).length > 0 ? (
                dashboardData.upcoming_events.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex flex-col items-center justify-center">
                      <span className="text-xs text-blue-400">
                        {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-foreground">
                        {new Date(event.start_date).getDate()}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground flex items-center gap-2 flex-wrap">
                        {event.title}
                        <BranchLabel name={event.branch_name} show={showBranchLabels} />
                      </h4>
                      <p className="text-sm text-muted-foreground">{event.event_type}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No upcoming events</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AttendanceTodayCard = ({ attendance }) => (
  <Card className="border-border">
    <CardHeader>
      <CardTitle className="text-foreground flex items-center gap-2">
        <Clock className="w-5 h-5" />
        Today&apos;s attendance
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{attendance?.present || 0}</p>
          <p className="text-sm text-muted-foreground">Present</p>
        </div>
        <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
          <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{attendance?.absent || 0}</p>
          <p className="text-sm text-muted-foreground">Absent</p>
        </div>
        <div className="text-center p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{attendance?.late || 0}</p>
          <p className="text-sm text-muted-foreground">Late</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const AnnouncementsCard = ({ items }) => (
  <Card className="border-border">
    <CardHeader>
      <CardTitle className="text-foreground">Recent announcements</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {(items || []).length > 0 ? (
          items.map((ann) => (
            <div key={ann.id} className="p-3 bg-muted/50 rounded-lg border border-border">
              <h4 className="font-medium text-foreground">{ann.title}</h4>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
            </div>
          ))
        ) : (
          <p className="text-muted-foreground text-center py-4">No announcements</p>
        )}
      </div>
    </CardContent>
  </Card>
);

const NotificationsCard = ({ items, homeworkPath = '/portal/student/homework' }) => {
  const { user } = useAuth();
  const [readIds, setReadIds] = useState(() => new Set());

  const handleNotificationClick = async (notif) => {
    if (!isNotificationUnread(notif) || !notif?.id) return;
    try {
      await markNotificationRead(notif.id);
      setReadIds((prev) => new Set(prev).add(notif.id));
      dispatchNotificationsRead();
    } catch {
      // Non-blocking
    }
  };

  const isUnread = (notif) => isNotificationUnread(notif) && !readIds.has(notif.id);

  return (
  <Card className="border-border">
    <CardHeader>
      <CardTitle className="text-foreground">Notifications</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {(items || []).length > 0 ? (
          items.map((notif) => (
            <Link
              key={notif.id}
              to={resolveNotificationLink(user, notif, homeworkPath)}
              onClick={() => handleNotificationClick(notif)}
              className={`block p-3 rounded-lg border transition-colors ${
                isUnread(notif)
                  ? 'bg-muted/50 border-border hover:border-primary/40'
                  : 'bg-muted/30 border-border/60 opacity-80 hover:border-primary/30'
              }`}
            >
              <h4 className={`font-medium ${isUnread(notif) ? 'text-foreground' : 'text-muted-foreground'}`}>
                {notif.title}
              </h4>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
            </Link>
          ))
        ) : (
          <p className="text-muted-foreground text-center py-4">No notifications</p>
        )}
      </div>
    </CardContent>
  </Card>
  );
};

const AssignmentsCard = ({ items, homeworkPath = '/portal/student/homework', showStudentName = false }) => (
  <Card className="border-border">
    <CardHeader className="flex flex-row items-center justify-between gap-2">
      <CardTitle className="text-foreground">Recent homework</CardTitle>
      <Link to={homeworkPath} className="text-xs text-primary hover:underline">View all</Link>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {(items || []).length > 0 ? (
          items.map((item) => (
            <Link
              key={item.id}
              to={homeworkPath}
              className="block p-3 bg-muted/50 rounded-lg border border-border hover:border-primary/40 transition-colors"
            >
              <h4 className="font-medium text-foreground">{item.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {showStudentName && item.student_name ? `${item.student_name} · ` : ''}
                {item.subject_name || 'Subject'}{item.class_name ? ` · ${item.class_name}` : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Due {item.due_date || '—'}</p>
            </Link>
          ))
        ) : (
          <p className="text-muted-foreground text-center py-4">No homework posted yet</p>
        )}
      </div>
    </CardContent>
  </Card>
);

const EventsCard = ({ events }) => (
  <Card className="border-border">
    <CardHeader>
      <CardTitle className="text-foreground flex items-center gap-2">
        <Calendar className="w-5 h-5" />
        Upcoming events
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {(events || []).length > 0 ? (
          events.map((event) => (
            <div key={event.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex flex-col items-center justify-center">
                <span className="text-xs text-blue-400">
                  {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short' })}
                </span>
                <span className="text-lg font-bold text-foreground">
                  {new Date(event.start_date).getDate()}
                </span>
              </div>
              <div>
                <h4 className="font-medium text-foreground">{event.title}</h4>
                <p className="text-sm text-muted-foreground">{event.event_type}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="text-muted-foreground text-center py-4">No upcoming events</p>
        )}
      </div>
    </CardContent>
  </Card>
);

const StatCard = ({ title, value, icon: Icon, color, to }) => {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
    green: 'bg-green-500/10 border-green-500/30 text-green-500',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-500',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
  };

  const card = (
    <Card className={`border-border ${to ? 'hover:border-primary/40 hover:shadow-sm transition-shadow' : ''}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-2xl font-bold text-foreground">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
      >
        {card}
      </Link>
    );
  }

  return card;
};

export default Dashboard;
